import { isNil, isNotNil } from "@etudescarre/utils";
import {
  DownloadSimple as DownloadIcon,
  X as XIcon,
} from "@phosphor-icons/react";
import { captureException } from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/api/client";

export type IfcViewerProps = {
  yardId: string;
  fichierIfcId: number;
  /** Maquette shown in the toolbar, e.g. "MAQ-01 - Structure". */
  title: string;
  onDownload: (fichierIfcId: number) => void;
  onClose: () => void;
};

type ViewerStatus = "loading" | "ready" | "error";

export function IfcViewer({
  yardId,
  fichierIfcId,
  title,
  onDownload,
  onClose,
}: IfcViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<ViewerStatus>("loading");

  useEffect(() => {
    let cancelled = false;
    let dispose: (() => void) | undefined;

    async function load() {
      setStatus("loading");
      try {
        const [{ data, error }, { mountIfcScene }] = await Promise.all([
          apiClient.GET("/api/yards/{yardId}/fichiers/{fichierId}/content", {
            params: { path: { yardId, fichierId: String(fichierIfcId) } },
            parseAs: "arrayBuffer",
          }),
          import("./ifc-scene"),
        ]);
        if (cancelled) return;
        const container = containerRef.current;
        if (isNotNil(error) || isNil(data) || isNil(container)) {
          throw new Error(`Failed to fetch IFC file ${fichierIfcId}`);
        }

        const cleanup = await mountIfcScene(container, new Uint8Array(data));
        if (cancelled) {
          cleanup();
          return;
        }
        dispose = cleanup;
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to display IFC file", err);
        captureException(err);
        setStatus("error");
      }
    }

    load();
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, [yardId, fichierIfcId]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80">
      <div className="flex items-center justify-between gap-4 bg-zinc-900 px-4 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="truncate text-sm text-zinc-200" title={title}>
            {title}
          </span>
          <button
            type="button"
            onClick={() => onDownload(fichierIfcId)}
            className="flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <DownloadIcon size={16} />
            Télécharger le fichier IFC
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer la visionneuse"
          className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white"
        >
          <XIcon size={20} />
        </button>
      </div>

      <div className="relative flex-1 bg-zinc-100 dark:bg-zinc-800">
        <div
          ref={containerRef}
          className="absolute inset-0"
          data-testid="ifc-viewer-canvas"
        />
        {status === "loading" && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-zinc-600 dark:text-zinc-300">
            Chargement de la maquette…
          </p>
        )}
        {status === "error" && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-red-600 dark:text-red-400">
            Impossible d'afficher la maquette. Vous pouvez télécharger le
            fichier IFC.
          </p>
        )}
      </div>
    </div>
  );
}
